import { RadioTower } from 'lucide-react';

import { mensagemDeErro } from '@/lib/erros';
import { formatarDataHora } from '@/lib/formato';
import { minutosDeAgora } from '@/lib/fuso';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { ReguaDia } from '@/ui/components/regua-dia';
import { Status } from '@/ui/components/status';
import { SecaoFormulario } from '@/ui/patterns/secao-formulario';
import { Skeleton } from '@/ui/primitives/skeleton';
import {
  type ConfiguracaoCapturaResposta,
  type EmpresaDetalhe,
  horariosDoDia,
  INTERVALO_MINIMO_CAPTURA_MINUTOS,
} from '@fiscoai/contracts';

import { useConfiguracaoCaptura } from './captura/api';
import { descreverAgenda } from './captura/descrever-agenda';
import { FormularioCaptura } from './captura/formulario-captura';
import { ProximasConsultas } from './captura/proximas';
import { SituacaoAtual } from './captura/situacao-atual';

function Leitura({ c }: { c: ConfiguracaoCapturaResposta }) {
  return (
    <SecaoFormulario
      titulo="Captura automática"
      descricao="O FiscoAI busca na SEFAZ as notas emitidas contra este CNPJ. Quem gerencia a captura da empresa muda a agenda."
    >
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Status tom={c.ativa ? 'sucesso' : 'neutro'}>{c.ativa ? 'Ligada' : 'Desligada'}</Status>
        <span className="text-texto-suave">
          {c.cienciaAutomatica ? 'Ciência automática' : 'Ciência manual'}
          {c.atualizadaEm &&
            ` · alterada em ${formatarDataHora(c.atualizadaEm)}${c.atualizadaPor ? ` por ${c.atualizadaPor.nome}` : ''}`}
        </span>
      </div>
      <ReguaDia
        horarios={horariosDoDia(c.agenda)}
        esperaMinutos={INTERVALO_MINIMO_CAPTURA_MINUTOS}
        agoraMinutos={minutosDeAgora(c.fuso)}
        legenda={descreverAgenda(c.agenda)}
      />
      <ProximasConsultas
        proximas={c.proximasExecucoes}
        fuso={c.fuso}
        aviso={c.ativa ? null : 'Captura desligada.'}
      />
    </SecaoFormulario>
  );
}

/** Aba "Captura SEFAZ" do cadastro (spec 0006): situação atual e a agenda, com a régua do dia. */
export function AbaCaptura(props: {
  empresa: EmpresaDetalhe;
  podeGerenciar: boolean;
  podeEnviarCertificado: boolean;
}) {
  return (
    <div className="grid gap-4">
      <SituacaoAtual empresaId={props.empresa.id} />
      <Agenda {...props} />
    </div>
  );
}

function Agenda({
  empresa,
  podeGerenciar,
  podeEnviarCertificado,
}: {
  empresa: EmpresaDetalhe;
  podeGerenciar: boolean;
  podeEnviarCertificado: boolean;
}) {
  const consulta = useConfiguracaoCaptura(empresa.id);
  if (consulta.isError)
    return (
      <EstadoVazio
        tom="perigo"
        icone={<RadioTower />}
        titulo="Não foi possível carregar a captura"
        texto={mensagemDeErro(consulta.error)}
      />
    );
  if (!consulta.data) return <Skeleton className="h-80 rounded-painel" />;
  if (!podeGerenciar) return <Leitura c={consulta.data} />;
  return (
    <FormularioCaptura
      key={consulta.data.atualizadaEm ?? 'padrao'}
      empresa={empresa}
      salva={consulta.data}
      podeEnviarCertificado={podeEnviarCertificado}
    />
  );
}
